import { ChangeDetectionStrategy, Component, computed, effect, input, OnDestroy, output, viewChild } from '@angular/core';
import { GoogleMap, MapAdvancedMarker } from '@angular/google-maps';
import { CvMarkerType, getCvMarkerUrl, isVerifiedMarker, MapMarkerData } from '../../map-utils';
import { CoordinateHistory } from '../../../../../models/coordinate-history';
import { formatDate } from '../../../../../core/utils/date-fns-utils';
import { VALIDATED_MARKER_ID } from '../../../constants';

@Component({
  selector: 'app-cv-marker',
  standalone: true,
  imports: [MapAdvancedMarker],
  templateUrl: './cv-marker.component.html',
  styleUrl: './cv-marker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CvMarkerComponent implements OnDestroy {
  mapAdvancedMarker = viewChild<MapAdvancedMarker>('mapAdvancedMarker');

  googleMapDirective = input.required<GoogleMap>();
  mapsFullyRendered = input.required<boolean>();
  marker = input.required<MapMarkerData>();
  isHovered = input.required<boolean>();
  minimizeMarkers = input.required<boolean>();
  validationHistory = input.required<CoordinateHistory[]>();
  streetViewActive = input.required<boolean>();

  markerClick = output<void>();
  markerDrag = output<google.maps.MapMouseEvent>();
  markerMouseover = output<void>();
  markerMouseout = output<void>();

  protected readonly getCvMarkerUrl = getCvMarkerUrl;
  protected readonly cvMarkerType = CvMarkerType;
  protected readonly isVerifiedMarker = isVerifiedMarker;

  private panoMarker: google.maps.Marker;

  // put config here that does not need to be reactive, component does not notice changes
  // if it's not re-rendered ... use individual input for reactive config like zIndex, etc.
  protected readonly markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpClickable: true,
  };

  protected readonly validationHistoryItem = computed(() => {
    const marker = this.marker();
    const validationHistory = this.validationHistory();

    if (marker.id === VALIDATED_MARKER_ID) return validationHistory?.[0];

    return validationHistory?.find(lch =>
        String(lch.id) === marker.id);
  });


  // hovering over a marker should show it full scale
  protected readonly isMinimized = computed(() => this.minimizeMarkers() &&
      !this.isHovered());

  protected readonly zIndex = computed(() => {
    const isHovered = this.isHovered();
    const marker = this.marker();

    return isHovered ?
        1000 :
        marker.id === CvMarkerType.CUSTOM ?
            999 :
            isVerifiedMarker(marker.id) ?
                998 : null;
  });


  protected readonly title = computed((): string => {
    const marker = this.marker();
    const validationHistoryItem = this.validationHistoryItem();
    let title = '';

    if ((marker.type === CvMarkerType.HISTORY || marker.id === VALIDATED_MARKER_ID) &&
        validationHistoryItem) {
      title = `Validated on: ${formatDate(new Date(validationHistoryItem?.createdDateTime),
          'dd.MM.yyyy, HH:mm')}\n`;
    }

    title += `Latitude: ${marker.position.lat}\nLongitude: ${marker.position.lng}`;
    return title;
  });

  protected readonly gmpDraggable = computed(() => this.marker().id === CvMarkerType.CUSTOM);

  protected readonly panoMarkerOptions = computed((): google.maps.MarkerOptions => {
    const marker = this.marker();
    const locationCoordinateHistoryItem = this.validationHistoryItem();
    const dblSize = new google.maps.Size(80, 112);
    const zIndex = this.zIndex();

    return {
      opacity: marker.type === CvMarkerType.HISTORY ? 0.7 : 1,
      position: marker.position,
      icon: {
        url: getCvMarkerUrl(marker.type === CvMarkerType.HISTORY ?
                locationCoordinateHistoryItem?.source :
                marker.type,
            isVerifiedMarker(marker.id),
            false),
        size: dblSize,
        scaledSize: dblSize,
      },
      zIndex,
    };
  });

  constructor() {
    // creates LEGACY markers to put onto street view as advanced map markers are not supported
    // https://issuetracker.google.com/issues/282790637
    effect(() => {
      const isStreetViewActive = this.streetViewActive();
      const panoMarkerOptions = this.panoMarkerOptions();
      const googleMap = this.googleMapDirective();
      const rdy = this.mapsFullyRendered();

      if (rdy && isStreetViewActive) {
        if (this.panoMarker) {
          this.panoMarker.setOptions(panoMarkerOptions);
          this.panoMarker.setMap(googleMap.getStreetView());
        } else {
          this.panoMarker = new google.maps.Marker(panoMarkerOptions);
          this.panoMarker.setMap(googleMap.getStreetView());
        }
      } else {
        this.panoMarker?.setMap(null);
        this.panoMarker = null;
      }
    });
  }

  public ngOnDestroy() {
    this.panoMarker?.setMap(null);
    this.panoMarker = null;
  }

  protected onMarkerClick() {
    if (this.marker().type !== CvMarkerType.AUTOCOMPLETE) {
      this.markerClick.emit();
    }
  }

}
